import {
  ArticlePublishError,
  beginPublish,
  endPublish,
  isPublishUnresolved,
  isSafeToRetry,
  publishArticleDirect,
} from './shopifyArticleApi';
import { publishSocialDirect } from './socialPublishApi';
import {
  isErrorEnvelope,
  isSocialSafeToRetry,
  socialPublishErrorFromMessage,
  socialSetupErrorFromEnvelope,
} from '@lib/services/social/publishErrors';
// [3.5 #4] 带 `{product_url}` 字样的正文一个字都不能发出去。
import {
  placeholderBlockMessage,
  unresolvedProductPlaceholders,
} from '@lib/utils/productLinks';

/**
 * Button publish = the backend, for EVERY selected channel (Monica, 2026-08-10).
 *
 * <p>One implementation for both entry points — Content Studio's draft card/editor and the
 * Hub's publish modal — because the previous shape (each entry re-deciding which channels it
 * could handle) is exactly how "the same action, two reliabilities" happened. The agent
 * publishes only when the merchant TYPES the request in chat; a button click carries content
 * that is already reviewed and fixed, and no fixed content should ride on a model emitting
 * the right tool call.
 *
 * <p>Channels are independent. A mixed selection used to be handed to the agent whole, on the
 * argument that publishing only the Shopify half would silently drop the rest — the concern
 * was right and the resolution backwards. Here every channel gets its own deterministic call,
 * its own outcome, its own latch, and its own retry: Shopify succeeding and Instagram failing
 * is a real state, and the merchant sees exactly that instead of one blended answer.
 *
 * <p>No fallback to the agent on failure, for the same reason the article path never had one:
 * a timeout cannot be told apart from a slow success, and re-publishing through Maya is how a
 * second copy lands on the merchant's blog or feed.
 */

/** The channel labels the publish dialogs offer. Everything else has no one-click path. */
export const SHOPIFY_CHANNEL = 'Shopify Store';
const SOCIAL_PLATFORM_BY_LABEL: Record<string, string> = {
  Instagram: 'instagram',
  Facebook: 'facebook',
};

export interface DirectPublishDraft {
  title: string;
  content: string;
  summary?: string;
  tags?: string[];
  image_url?: string;
  media?: { image_key?: string; image_url?: string; role?: string }[];
}

export interface ChannelOutcome {
  label: string;
  ok: boolean;
  /** The live URL, when the channel reported one — feeds the Published card. */
  url?: string;
  /** The platform-side id (FB `PageID_PostID`, IG media id, Shopify article id) —
   *  what the metrics puller keys on, threaded through to publish bookkeeping. */
  externalId?: string;
  /** Present on failure: what the merchant is told. */
  message?: string;
  /** The raw error, so entry points can run their own classifiers (e.g. Shopify scope). */
  error?: unknown;
  /** False when the outcome is unknown — the channel is latched for the session. */
  safeToRetry?: boolean;
  /** True when the channel never ran because a publish for this draft is in flight or
   *  unresolved. Distinct from failure: nothing new happened. */
  blocked?: boolean;
  /**
   * The server refused because this piece is ALREADY live on the channel (contract v1.2 §5.4:
   * error message starting `ALREADY_PUBLISHED:`). Comes with `ok: true` — the piece IS
   * published, so the card shows it as published and offers no retry — but nothing new
   * happened: callers must not narrate a new publish or book it a second time.
   */
  alreadyPublished?: boolean;
}

/** The prefix chatbot-api puts on the refusal when the piece is already live (§5.4). */
export const ALREADY_PUBLISHED_CODE = 'ALREADY_PUBLISHED';

/**
 * An `ALREADY_PUBLISHED:` refusal, with the live URL the message carries when there is one.
 * The article path parses the prefix into `ArticlePublishError.code`; the social path's code
 * pattern is lowercase-only, so there the prefix is still at the start of the message.
 */
export function alreadyPublishedOf(err: unknown): { url?: string } | null {
  const coded = err instanceof ArticlePublishError && err.code === ALREADY_PUBLISHED_CODE;
  const message = err instanceof Error ? err.message : '';
  if (!coded && !message.startsWith(`${ALREADY_PUBLISHED_CODE}:`)) return null;
  const url = /\((https?:\/\/[^\s)]+)\)/.exec(message)?.[1];
  return url ? { url } : {};
}

/** The cover, by the same precedence everywhere: role='cover' first, else the first entry. */
function cover(media: DirectPublishDraft['media']) {
  if (!media?.length) return undefined;
  return media.find(m => (m.role ?? 'inline') === 'cover') ?? media[0];
}

const IN_FLIGHT_MESSAGE = 'This post is already being published to this channel. Give it a moment.';
const UNRESOLVED_MESSAGE =
  'An earlier publish to this channel never reported back, so publishing again could post a duplicate. Check the channel before retrying.';

/**
 * The social endpoint's answer, unwrapped. chatbot-api's interceptor resolves most error
 * responses into a {errorCode, errorMessage} envelope with the status masked as 200 — the
 * same trap the article path documents — so a response without `ok: true` IS a failure, and
 * treating it as success would synthesise a Published card for a post that never existed.
 *
 * Failures come out TYPED: a setup failure the merchant can fix throws SocialSetupError so
 * callers can offer the dialog, and everything else throws SocialPublishError carrying the
 * backend code that decides whether a retry could double-post. Both used to be one anonymous
 * Error whose message was re-parsed by substring further down.
 */
function unwrapSocial(res: unknown): { post_url?: string; post_id?: string } {
  if (isErrorEnvelope(res)) {
    const setupError = socialSetupErrorFromEnvelope(res);
    if (setupError) throw setupError;
    throw socialPublishErrorFromMessage(String(res.errorMessage ?? 'The channel did not confirm the post.'));
  }
  const data = res as { ok?: boolean; post_url?: string; post_id?: string } | null;
  if (data?.ok !== true) {
    // A 2xx that isn't the success shape. Uncoded on purpose: we cannot tell an empty
    // body from a slow success, so this must not offer a retry.
    throw socialPublishErrorFromMessage('The channel did not confirm the post.');
  }
  return data;
}

/**
 * Publish one reviewed draft to every selected channel, each deterministically.
 *
 * <p>Latching is per (draft, channel): `draftKeys` are every id this draft is known by (the
 * agent's `draft_<hex>`, the backend row id), and each channel reserves `<id>::<label>` so a
 * publish started in Content Studio still blocks the Hub's button for THAT channel — while
 * leaving the merchant free to retry a DIFFERENT channel that failed.
 *
 * <p>Never rejects: the per-channel outcome list is the whole point.
 */
export async function publishToChannels(params: {
  tenantId: string;
  channels: string[];
  draft: DirectPublishDraft;
  /** Backend row id — what archives the publish against the exact draft. */
  draftRowId?: string;
  /** Every id this draft is known by, for the session latch. */
  draftKeys: (string | null | undefined)[];
  /** Publish again even where it is already live (contract v1.2 §5.4). Published page only. */
  republish?: boolean;
}): Promise<ChannelOutcome[]> {
  const { tenantId, channels, draft, draftRowId, draftKeys, republish } = params;

  /* [3.5 #4] 预检:正文里的 `{product_url}` 没被替换 —— PRD 把这一篇算作**生成失败**,
     不允许进入发布。放在 beginPublish 之前是关键:什么都没发生,所以既不上闸也不占
     latch,商家让 Maya 重生成之后可以照常发。

     为什么闸在这里而不在两个 API 里:publishToChannels 是所有**按钮发布**的唯一漏斗
     (Chat 的单篇卡/任务卡、Hub 的 DraftPublishModal、以及 publishSignal 走的那条),
     一处拦住就全覆盖。日历的 Publish Now 不经过这里(publishNowJob 直接调两个 API),
     它自己那条循环里读同一个 unresolvedProductPlaceholders —— 判定口径只有一份。 */
  const unresolved = unresolvedProductPlaceholders(draft.content);
  if (unresolved.length > 0) {
    const message = placeholderBlockMessage(unresolved);
    return channels.map(label => ({
      label,
      ok: false,
      // 可重试:这不是「结果未知」那一类,是内容不合格 —— 重生成之后就该能发。
      safeToRetry: true,
      message,
    }));
  }

  const keys = draftKeys.filter((k): k is string => !!k);
  const keysFor = (label: string) =>
    (keys.length ? keys : ['__unkeyed_draft__']).map(k => `${k}::${label}`);

  return Promise.all(
    channels.map(async (label): Promise<ChannelOutcome> => {
      const channelKeys = keysFor(label);
      const token = beginPublish(channelKeys);
      if (!token) {
        return {
          label,
          ok: false,
          blocked: true,
          safeToRetry: false,
          message: isPublishUnresolved(channelKeys) ? UNRESOLVED_MESSAGE : IN_FLIGHT_MESSAGE,
        };
      }
      try {
        if (label === SHOPIFY_CHANNEL) {
          const res = await publishArticleDirect({
            tenantId,
            title: draft.title,
            bodyMarkdown: draft.content,
            summary: draft.summary,
            tags: draft.tags,
            imageKey: cover(draft.media)?.image_key,
            imageUrl: cover(draft.media)?.image_url ?? draft.image_url,
            draftId: draftRowId,
            republish,
          });
          endPublish(token, true);
          return { label, ok: true, url: res.article_url, externalId: res.article_id };
        }

        const platform = SOCIAL_PLATFORM_BY_LABEL[label];
        if (!platform) {
          // No one-click path for this channel. Honest and unlatched: nothing was attempted,
          // and the chat path remains available for the merchant to ask for it in words.
          endPublish(token, true);
          return {
            label,
            ok: false,
            safeToRetry: true,
            message: `${label} has no one-click publish yet — ask Maya in chat to publish there.`,
          };
        }

        const res = unwrapSocial(
          await publishSocialDirect({
            tenantId,
            platform,
            message: draft.content,
            imageUrl: cover(draft.media)?.image_url ?? draft.image_url,
            title: draft.title,
            draftId: draftRowId,
            republish,
          })
        );
        endPublish(token, true);
        return { label, ok: true, url: res.post_url, externalId: res.post_id };
      } catch (err) {
        // Already live on this channel (§5.4): shown as published, not as a failure to retry.
        const already = alreadyPublishedOf(err);
        if (already) {
          endPublish(token, true);
          return { label, ok: true, alreadyPublished: true, url: already.url };
        }
        const safe =
          label === SHOPIFY_CHANNEL || err instanceof ArticlePublishError
            ? isSafeToRetry(err)
            : isSocialSafeToRetry(err);
        endPublish(token, safe);
        return {
          label,
          ok: false,
          error: err,
          safeToRetry: safe,
          message: err instanceof Error ? err.message : String(err),
        };
      }
    })
  );
}
